import { type PropsWithChildren } from "react";

import { cssMap } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";

import { useVerticalScrollDirection } from "./use-vertical-scroll-direction";

const TOP = "top";
const BOTTOM = "bottom";

export type ScrollUpBarProps = PropsWithChildren<{
    anchor?: typeof TOP | typeof BOTTOM;
    sticky?: boolean;
}>;

const ScrollUpBarStyleMap = cssMap({
    base: {
        transition: "transform .25s ease-in-out",
        transform: "translateY(0)",
        position: "fixed"
    },
    sticky: { position: "sticky" },
    top: { inset: "0 0 auto 0" },
    bottom: { inset: "auto 0 0 0" },
    stuck: { transition: "transform .5s ease-in-out" }
});

const ScrollUpBarHiddenStyleMap = cssMap({
    top: { transform: "translateY(-100%)" },
    bottom: { transform: "translateY(100%)" }
});

const ScrollUpBarBackgroundStyleMap = cssMap({
    base: {
        "backgroundColor": "#fff",
        "&::after": {
            content: "",
            position: "absolute",
            pointerEvents: "none",
            inset: 0,
            opacity: 0,
            transition: "opacity .25s ease-in-out"
        }
    },
    top: {
        "&::after": {
            pointerEvents: "none",
            // eslint-disable-next-line local/ensure-wac-token-usage
            boxShadow: "0 1px 10px -2px rgba(0,0,0,.3)"
        }
    },
    bottom: {
        "&::after": {
            pointerEvents: "none",
            // eslint-disable-next-line local/ensure-wac-token-usage
            boxShadow: "0 -1px 10px -2px rgba(0,0,0,.3)"
        }
    },
    stuck: {
        "&::after": {
            opacity: 1,
            pointerEvents: "none",
            transition: "opacity .5s ease-in-out"
        }
    }
});

/**
 * `ScrollUpBar` allows for an overlay of content to be displayed
 * when the user scrolls up, and hidden when the user scrolls down.
 */
export const ScrollUpBar = ({
    children,
    sticky,
    anchor = BOTTOM
}: ScrollUpBarProps) => {
    const [scrollRef, direction] = useVerticalScrollDirection();
    const [topRef, topInView, topEntry] = useInView();
    const [bottomRef, bottomInView, bottomEntry] = useInView();

    // The `ScrollUpBar` is  always 'stuck' (aka fixed) by default.
    let stuck = true;

    if (sticky) {
        // If the `ScrollUpBar` is sticky, it should only be 'stuck'
        // if it is not in view. Two sentinel elements are observed
        // (using `IntersectionObserver`) to determine if the `ScrollUpBar`
        // is in view. If the intersection observers haven't
        // initialized yet, default to assuming the bar is in view,
        // so that any 'stuck' state is not applied too eagerly.
        stuck = !(
            topInView ||
            topEntry == null ||
            bottomInView ||
            bottomEntry == null
        );
    }

    // The `ScrollUpBar` should only be hidden if it is stuck
    // and the user is scrolling down.
    const hidden = stuck && direction == "down";

    const containerStyle = [
        ScrollUpBarStyleMap.base,
        ScrollUpBarStyleMap[anchor],
        sticky && ScrollUpBarStyleMap.sticky,
        stuck && ScrollUpBarStyleMap.stuck,
        hidden && ScrollUpBarHiddenStyleMap[anchor]
    ];

    const bgStyle = [
        ScrollUpBarBackgroundStyleMap.base,
        ScrollUpBarBackgroundStyleMap[anchor],
        stuck && ScrollUpBarBackgroundStyleMap.stuck
    ];

    return (
        <>
            {sticky ? (
                <div
                    ref={topRef}
                    aria-hidden="true"
                    style={{ pointerEvents: "none", visibility: "hidden" }}
                />
            ) : null}

            <div ref={scrollRef} css={containerStyle}>
                <div css={bgStyle} />
                {children}
            </div>

            {sticky ? (
                <div
                    ref={bottomRef}
                    aria-hidden="true"
                    style={{ pointerEvents: "none", visibility: "hidden" }}
                />
            ) : null}
        </>
    );
};
